import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { RecurringDocumentDetail as Detail, RecurringDocumentRun } from '../../interfaces/recurringDocument.interface';
import { RecurringDocumentService } from '../../services/recurringDocument.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ScrollableTable from '../common/ScrollableTable';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import { describeSchedule, statusColor } from '../accounting/recurringJournalText';
import { KIND_LABEL, modeLabel } from './recurringDocumentText';
import RecurringDocumentForm from './RecurringDocumentForm';

const th = { padding: '10px', textAlign: 'left' as const, backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' };
const td = { padding: '10px', borderBottom: '1px solid #ddd', verticalAlign: 'top' as const };

const DOC_PATH: Record<string, string> = {
  SALES_INVOICE: '/sales/invoices', QUOTATION: '/sales/quotations', SUPPLIER_BILL: '/procurement/supplier-bills',
};

const RecurringDocumentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canInvoice = usePermission(PERMISSIONS.SALES_INVOICE_MANAGE);
  const canQuote = usePermission(PERMISSIONS.QUOTATION_MANAGE);
  const canBill = usePermission(PERMISSIONS.SUPPLIER_BILL_CREATE);
  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [linkRun, setLinkRun] = useState<RecurringDocumentRun | null>(null);
  const [linkId, setLinkId] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setData(await RecurringDocumentService.get(id));
    } catch (e) {
      toast.showApiError(e, 'Failed to load the template');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!data) return <Box sx={{ p: 3 }}><Alert severity="error">Template not found.</Alert></Box>;
  const t = data.template;
  const canManage = t.kind === 'SALES_INVOICE' ? canInvoice : t.kind === 'QUOTATION' ? canQuote : canBill;
  const backPath = t.kind === 'SUPPLIER_BILL' ? '/procurement/recurring-bills' : '/sales/recurring';

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); toast.showSuccess(ok); await load(); }
    catch (e) { toast.showApiError(e, 'The action failed'); }
    finally { setBusy(false); }
  };

  const runNow = () => act(async () => {
    const r = await RecurringDocumentService.runNow(t.id);
    toast.showSuccess(r.message ?? `${r.generated} generated, ${r.failed} failed, ${r.pendingReview} awaiting review`);
  }, 'Run finished');

  const subtotal = t.lines.reduce((s, l) => {
    const gross = Number(l.quantity) * Number(l.unitPrice);
    const disc = l.discountPct != null ? gross * Number(l.discountPct) / 100 : Number(l.discountAmount ?? 0);
    return s + Math.max(gross - disc, 0);
  }, 0);

  return (
    <Box data-help-page="recurringDocs.detail.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{t.name} <Chip size="small" label={t.status} color={statusColor(t.status)} sx={{ ml: 1 }} /></Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {canManage && t.status !== 'ENDED' && <Button data-help="recurringDocs.detail.action.edit" variant="outlined" onClick={() => setEditing(true)} disabled={busy}>Edit</Button>}
          {canManage && t.status === 'ACTIVE' && <Button data-help="recurringDocs.detail.action.runNow" variant="contained" onClick={runNow} disabled={busy}>Run now</Button>}
          {canManage && t.status === 'ACTIVE' && <Button data-help="recurringDocs.detail.action.pause" variant="outlined" onClick={() => act(() => RecurringDocumentService.pause(t.id), 'Paused')} disabled={busy}>Pause</Button>}
          {canManage && t.status === 'PAUSED' && <Button data-help="recurringDocs.detail.action.resume" variant="contained" onClick={() => act(() => RecurringDocumentService.resume(t.id), 'Resumed')} disabled={busy}>Resume</Button>}
          {canManage && t.status !== 'ENDED' && <Button data-help="recurringDocs.detail.action.end" variant="outlined" color="warning" onClick={() => act(() => RecurringDocumentService.end(t.id), 'Ended')} disabled={busy}>End</Button>}
          {canManage && (
            <Button data-help="recurringDocs.detail.action.delete" variant="outlined" color="error" disabled={busy}
              onClick={() => act(async () => { await RecurringDocumentService.remove(t.id); navigate(backPath); }, 'Deleted')}>Delete</Button>
          )}
          <Button variant="text" onClick={() => navigate(backPath)}>Back</Button>
        </Box>
      </Box>

      {t.status === 'PAUSED' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This template is paused{t.lastResult ? `: ${t.lastResult}` : ''}. Fix the cause (for example reactivate the party), then Resume.
          Its schedule has not moved, so no occurrence was lost.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box><Typography variant="caption" color="text.secondary">Kind</Typography><Typography>{KIND_LABEL[t.kind]}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">{t.kind === 'SUPPLIER_BILL' ? 'Supplier' : 'Customer'}</Typography><Typography>{t.partyName ?? '-'}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Schedule</Typography><Typography>{describeSchedule(t)}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Creates</Typography><Typography>{modeLabel(t.kind, t.mode)}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Next run</Typography><Typography>{t.nextRunDate ? formatDate(t.nextRunDate) : '-'}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Occurrences</Typography><Typography>{t.occurrencesGenerated}{t.maxOccurrences ? ` of ${t.maxOccurrences}` : ''}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Subtotal per run</Typography><Typography>{formatMoney(subtotal)}</Typography></Box>
        </Box>
      </Paper>

      <Paper data-help-section="recurringDocs.detail.lines" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Lines</Typography>
        <ScrollableTable>
          <table data-help-section="recurringDocs.detail.linesTable" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th data-help="recurringDocs.detail.lines.col.description" style={th}>Description</th><th data-help="recurringDocs.detail.lines.col.qty" style={th}>Qty</th><th data-help="recurringDocs.detail.lines.col.unitPrice" style={th}>Unit price</th><th data-help="recurringDocs.detail.lines.col.discount" style={th}>Discount</th></tr></thead>
            <tbody>
              {t.lines.map((l, i) => (
                <tr key={i}>
                  <td style={td}>{l.description}</td><td style={td}>{l.quantity}</td><td style={td}>{formatMoney(Number(l.unitPrice))}</td>
                  <td style={td}>{l.discountPct != null ? `${l.discountPct}%` : l.discountAmount ? formatMoney(Number(l.discountAmount)) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTable>
      </Paper>

      <Paper data-help-section="recurringDocs.detail.runs" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Runs</Typography>
        <ScrollableTable>
          <table data-help-section="recurringDocs.detail.runsTable" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th data-help="recurringDocs.detail.runs.col.occurrence" style={th}>Occurrence</th><th data-help="recurringDocs.detail.runs.col.status" style={th}>Status</th><th data-help="recurringDocs.detail.runs.col.document" style={th}>Document</th><th data-help="recurringDocs.detail.runs.col.message" style={th}>Message</th><th data-help="recurringDocs.detail.runs.col.created" style={th}>Created</th><th style={th} /></tr></thead>
            <tbody>
              {data.runs.length === 0 && <tr><td style={td} colSpan={6}>No runs yet.</td></tr>}
              {data.runs.map(r => (
                <tr key={r.id}>
                  <td style={td}>{formatDate(r.occurrenceDate)}</td>
                  <td style={td}><Chip size="small" label={r.status.replace('_', ' ')} color={statusColor(r.status === 'PENDING_REVIEW' ? 'PAUSED' : r.status)} /></td>
                  <td style={td}>
                    {r.documentId
                      ? <RouterLink data-help="recurringDocs.detail.runs.action.openDoc" to={`${DOC_PATH[t.kind]}/${r.documentId}`}>{r.documentNumber ?? 'Open'}</RouterLink>
                      : '-'}
                  </td>
                  <td style={td}>{r.message ?? ''}</td>
                  <td style={td}>{formatDateTime(r.createdAt)}</td>
                  <td style={td}>
                    {canManage && r.status === 'FAILED' && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button data-help="recurringDocs.detail.runs.action.retry" size="small" variant="outlined" disabled={busy} onClick={() => act(() => RecurringDocumentService.retryRun(r.id), 'Retried')}>Retry</Button>
                        <Button data-help="recurringDocs.detail.runs.action.skip" size="small" variant="text" disabled={busy} onClick={() => act(() => RecurringDocumentService.skipRun(r.id), 'Skipped')}>Skip</Button>
                      </Box>
                    )}
                    {canManage && r.status === 'PENDING_REVIEW' && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button data-help="recurringDocs.detail.runs.action.createBill" size="small" variant="contained" onClick={() => navigate('/procurement/supplier-bills/new-service')}>Create bill</Button>
                        <Button data-help="recurringDocs.detail.runs.action.linkBill" size="small" variant="outlined" onClick={() => { setLinkRun(r); setLinkId(''); }}>Link bill</Button>
                        <Button data-help="recurringDocs.detail.runs.action.skip" size="small" variant="text" disabled={busy} onClick={() => act(() => RecurringDocumentService.skipRun(r.id), 'Skipped')}>Skip</Button>
                      </Box>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTable>
      </Paper>

      {editing && (
        <RecurringDocumentForm open kind={t.kind} template={t} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />
      )}
      <Dialog data-help-page="recurringDocs.link.page" open={!!linkRun} onClose={() => setLinkRun(null)}>
        <DialogTitle>Link a bill to this occurrence</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Create the bill, then paste its id (the last part of the bill page address) to close this run.</Typography>
          <TextField data-help="recurringDocs.link.field.billId" label="Bill id" value={linkId} onChange={e => setLinkId(e.target.value.trim())} sx={{ minWidth: 300 }} />
        </DialogContent>
        <DialogActions>
          <Button data-help="recurringDocs.link.cancel" onClick={() => setLinkRun(null)}>Cancel</Button>
          <Button data-help="recurringDocs.link.save" variant="contained" disabled={!linkId || busy}
            onClick={() => { const r = linkRun!; setLinkRun(null); act(() => RecurringDocumentService.linkRun(r.id, linkId), 'Linked'); }}>Link</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecurringDocumentDetail;
